import {
  computed,
  defineStore,
  useNuxtApp,
  useRuntimeConfig,
  useSupabase,
  useSupabaseUser,
  useRoute,
} from "#imports";
import { getIp } from "./utils";

type AuthProviders = "google" | "facebook";

export const useAuth = defineStore("auth", () => {
  const supabase = useSupabase();
  const config = useRuntimeConfig();
  const route = useRoute();

  /** supabase user information
   * @see https://supabase.nuxtjs.org/composables/usesupabaseuser
   */
  const authUser = useSupabaseUser();

  const isLoggedIn = computed(() => !!authUser.value);

  /** Create supabase account */
  const signUp = async (
    payload: Parameters<typeof supabase.auth.signUp>[0],
  ) => {
    const {
      error,
      data: { user },
    } = await supabase.auth.signUp(payload);
    if (error || !user) {
      throw error;
    }

    await supabase
      .from("privacy_policy_agreement_audit_log")
      .insert({
        user_id: user.id,
        terms_version: "1.0",
        ip: (await getIp()) ?? "Could not get IP address",
      })
      .throwOnError();
  };

  /** Login to supabase account */
  const signInWithPassword = async (
    payload: Parameters<typeof supabase.auth.signInWithPassword>[0],
  ) => {
    const { error } = await supabase.auth.signInWithPassword(payload);
    if (error) throw error;
  };

  /** Login to supabase account */
  const signInWithMagicLink = async (payload: { email: string }) => {
    const { error, data } = await supabase.auth.signInWithOtp({
      email: payload.email,
      options: {
        shouldCreateUser: false,
        // emailRedirectTo: 'https://example.com/welcome',
      },
    });
    if (error) {
      throw error;
    }
    return data;
  };

  const signInWithOAuth = async (provider: AuthProviders) => {
    const baseUrl = useNuxtApp().$config.public.baseURL;
    if (provider === "google") {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${baseUrl}${route.fullPath}`,
        },
      });
      if (error) {
        throw error;
      }
    } else if (provider === "facebook") {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${baseUrl}/confirm`,
        },
      });
      if (error) {
        throw error;
      }
    } else {
      throw new Error("Illegal OAuth provider: " + provider);
    }
  };

  const forgotPassword = async (payload: { email: string }) => {
    const { error } = await supabase.auth.resetPasswordForEmail(payload.email, {
      redirectTo: `${config.public.baseURL}/confirm`,
    });
    if (error) {
      throw error;
    }
  };

  const verifyOtp = async (
    payload: Parameters<typeof supabase.auth.verifyOtp>[0],
  ) => {
    const { error } = await supabase.auth.verifyOtp(payload);
    if (error) {
      throw error;
    }
  };

  const resetPassword = async (payload: { password: string }) => {
    const { error } = await supabase.auth.updateUser({
      password: payload.password!,
    });
    if (error) {
      throw error;
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const creatorApplication = async (payload: {
    file: File | null;
    url: string | null;
  }) => {
    if (!authUser.value?.sub) {
      throw new Error("AuthUser not found");
    }

    const formData = new FormData();
    if (payload.file) {
      formData.append("file", payload.file);
    }
    if (payload.url) {
      formData.append("url", payload.url);
    }

    const { error } = await supabase.functions.invoke("creator-application", {
      method: "POST",
      body: formData,
    });

    if (error) {
      throw error;
    }

    await supabase
      .from("terms_audit_log")
      .insert([
        {
          ip: (await getIp()) ?? "Could not get IP address",
          terms_version: "1.0",
          user_id: authUser.value.sub,
        },
      ])
      .throwOnError();

    await supabase
      .from("profiles")
      .update({ is_onboarded: true })
      .eq("id", authUser.value.sub)
      .throwOnError();
  };

  return {
    authUser,
    isLoggedIn,
    signUp,
    signInWithPassword,
    signInWithMagicLink,
    signInWithOAuth,
    forgotPassword,
    verifyOtp,
    resetPassword,
    signOut,
    creatorApplication,
  };
});
